import {
  deviceOrientationPermissionDeniedMessage,
  deviceOrientationPermissionRequired,
  isDeviceOrientationSupported,
} from './device-orientation-platform';

const HEADING_CAPTURE_TIMEOUT_MS = 1500;

type DeviceOrientationEventWithPermission = typeof DeviceOrientationEvent & {
  requestPermission?: () => Promise<'granted' | 'denied' | 'default'>;
};

type DeviceOrientationEventWithCompass = DeviceOrientationEvent & {
  webkitCompassHeading?: number;
};

function normalizeHeading(value: number): number {
  const normalized = value % 360;
  return normalized < 0 ? normalized + 360 : normalized;
}

function readHeading(event: DeviceOrientationEvent): number | null {
  const withCompass = event as DeviceOrientationEventWithCompass;
  if (
    typeof withCompass.webkitCompassHeading === 'number' &&
    Number.isFinite(withCompass.webkitCompassHeading)
  ) {
    return normalizeHeading(withCompass.webkitCompassHeading);
  }

  if (
    event.absolute &&
    typeof event.alpha === 'number' &&
    Number.isFinite(event.alpha)
  ) {
    return normalizeHeading(event.alpha);
  }

  return null;
}

export async function requestDeviceOrientationPermission(): Promise<boolean> {
  if (!isDeviceOrientationSupported()) {
    return false;
  }

  if (!deviceOrientationPermissionRequired()) {
    return true;
  }

  const ctor = DeviceOrientationEvent as DeviceOrientationEventWithPermission;
  try {
    const result = await ctor.requestPermission?.();
    return result === 'granted';
  } catch {
    return false;
  }
}

export function watchDeviceHeading(onHeading: (heading: number) => void): () => void {
  const handler = (event: Event) => {
    const heading = readHeading(event as DeviceOrientationEvent);
    if (heading != null) {
      onHeading(heading);
    }
  };

  window.addEventListener('deviceorientation', handler);
  window.addEventListener('deviceorientationabsolute', handler);

  return () => {
    window.removeEventListener('deviceorientation', handler);
    window.removeEventListener('deviceorientationabsolute', handler);
  };
}

function waitForDeviceHeading(timeoutMs: number): Promise<number | null> {
  return new Promise((resolve) => {
    let settled = false;

    const finish = (heading: number | null) => {
      if (settled) {
        return;
      }
      settled = true;
      stopWatch();
      clearTimeout(timeoutId);
      resolve(heading);
    };

    const stopWatch = watchDeviceHeading((heading) => finish(heading));
    const timeoutId = window.setTimeout(() => finish(null), timeoutMs);
  });
}

/**
 * One-shot read — only reliable when permission was already granted (e.g. Android).
 * On iOS, prefer useDeviceHeading with an upfront permission prompt.
 */
export async function getDeviceHeading(): Promise<number | null> {
  if (!isDeviceOrientationSupported()) {
    return null;
  }

  const permitted = await requestDeviceOrientationPermission();
  if (!permitted) {
    return null;
  }

  return waitForDeviceHeading(HEADING_CAPTURE_TIMEOUT_MS);
}

export function getDeviceOrientationErrorMessage(denied: boolean): string | null {
  if (!denied) {
    return null;
  }

  return deviceOrientationPermissionDeniedMessage();
}
